import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Checkbox,
  Popconfirm,
  Tooltip,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, SafetyOutlined } from "@ant-design/icons";
import { ThreeDot } from "react-loading-indicators";

import {
  getRoles,
  createRole,
  updateRole,
  deleteRole,
  addPermissionToRole,
  removePermissionFromRole,
} from "../../api/roles";
import { getPermissions } from "../../api/permissions";

// GET /roles returns [{ name: null, description: null }] for a role with no
// permissions (json_agg over a LEFT JOIN) instead of an empty array — filter those out.
const getAssignedPermissions = (role) =>
  (role?.permissions ?? []).filter((permission) => permission.name != null);

const isSuperAdminRole = (role) => role?.role_name?.toLowerCase() === "superadmin";

// Color-code by action so the same action always reads the same way, regardless of object.
const ACTION_COLORS = {
  Ver: "blue",
  Leer: "blue",
  Crear: "green",
  "Crear/Modificar": "gold",
  Modificar: "orange",
  Eliminar: "red",
};
const getActionColor = (actionName) => ACTION_COLORS[actionName] ?? "default";

// Groups a flat permission list into a Map of display_object_name -> permissions,
// preserving first-seen order.
const groupByObject = (permissionList) => {
  const groups = new Map();
  permissionList.forEach((permission) => {
    const objectName = permission.display_object_name ?? "Otros";
    if (!groups.has(objectName)) groups.set(objectName, []);
    groups.get(objectName).push(permission);
  });
  return groups;
};

// A role's assigned permissions only carry { name, description } — merge in the
// catalog's display_* fields before grouping so the table can cluster by object too.
const groupAssignedPermissionsByObject = (assignedPermissions, permissionsByNameMap) =>
  groupByObject(
    assignedPermissions.map((permission) => ({
      ...permission,
      ...permissionsByNameMap.get(permission.name),
    })),
  );

const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleModalLoading, setRoleModalLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm] = Form.useForm();

  const [permissionsRole, setPermissionsRole] = useState(null);
  const [togglingPermissionId, setTogglingPermissionId] = useState(null);

  // GET /roles' nested permissions only carry { name, description } — cross-reference
  // against the full catalog (which has the display_* fields) by name.
  const permissionsByName = useMemo(() => {
    const map = new Map();
    permissions.forEach((permission) => map.set(permission.name, permission));
    return map;
  }, [permissions]);

  const permissionGroups = useMemo(() => groupByObject(permissions), [permissions]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [rolesData, permissionsData] = await Promise.all([
          getRoles(),
          getPermissions(),
        ]);
        setRoles(rolesData);
        setPermissions(permissionsData);
      } catch (error) {
        console.log(error);
        message.error("No se pudieron cargar los roles.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [refresh]);

  useEffect(() => {
    if (!permissionsRole) return;
    const updated = roles.find((role) => role.id === permissionsRole.id);
    if (updated) setPermissionsRole(updated);
  }, [roles]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreateModal = () => {
    setEditingRole(null);
    roleForm.resetFields();
    setRoleModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    roleForm.setFieldsValue({ role_name: role.role_name });
    setRoleModalOpen(true);
  };

  const handleSaveRole = async (values) => {
    setRoleModalLoading(true);
    try {
      if (editingRole) {
        await updateRole(editingRole.id, values.role_name);
        message.success("Rol actualizado correctamente.");
      } else {
        await createRole(values.role_name);
        message.success("Rol creado correctamente.");
      }
      setRoleModalOpen(false);
      roleForm.resetFields();
      setRefresh((prev) => !prev);
    } catch (error) {
      console.log(error);
      message.error("No se pudo guardar el rol.");
    } finally {
      setRoleModalLoading(false);
    }
  };

  const handleDeleteRole = async (role) => {
    try {
      await deleteRole(role.id);
      message.success("Rol eliminado correctamente.");
      setRefresh((prev) => !prev);
    } catch (error) {
      console.log(error);
      message.error("No se pudo eliminar el rol.");
    }
  };

  const handleTogglePermission = async (permission, checked) => {
    setTogglingPermissionId(permission.id);
    try {
      if (checked) {
        await addPermissionToRole(permissionsRole.id, permission.id);
      } else {
        await removePermissionFromRole(permissionsRole.id, permission.id);
      }
      setRefresh((prev) => !prev);
    } catch (error) {
      console.log(error);
      message.error("No se pudo actualizar el permiso.");
    } finally {
      setTogglingPermissionId(null);
    }
  };

  const columns = [
    { title: "Rol", dataIndex: "role_name", key: "role_name" },
    {
      title: "Permisos",
      key: "permissions",
      render: (_, row) => {
        const assignedPermissions = getAssignedPermissions(row);
        if (assignedPermissions.length === 0) {
          return <span className="text-gray-400">Sin permisos</span>;
        }
        const grouped = groupAssignedPermissionsByObject(assignedPermissions, permissionsByName);
        return (
          <Space wrap size={[8, 8]}>
            {Array.from(grouped.entries()).map(([objectName, objectPermissions]) => (
              <div
                key={objectName}
                className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1"
              >
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {objectName}
                </span>
                <Space size={4}>
                  {objectPermissions.map((permission) => (
                    <Tag
                      color={getActionColor(permission.display_action_name)}
                      key={permission.name}
                      className="!m-0"
                    >
                      {permission.display_action_name ?? permission.name}
                    </Tag>
                  ))}
                </Space>
              </div>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip
            title={
              isSuperAdminRole(row)
                ? "El rol Superadmin solo puede modificarse mediante código"
                : ""
            }
          >
            <span>
              <Button
                size="small"
                icon={<SafetyOutlined />}
                disabled={isSuperAdminRole(row)}
                onClick={() => setPermissionsRole(row)}
              >
                Gestionar permisos
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(row)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este rol?"
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => handleDeleteRole(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          Crear rol
        </Button>
      </div>

      {isLoading ? (
        <div className="w-full text-center min-h-72 flex items-center justify-center">
          <ThreeDot variant="brick-stack" color="#4048F1" size="medium" />
        </div>
      ) : (
        <Table rowKey="id" columns={columns} dataSource={roles} pagination={{ pageSize: 10 }} />
      )}

      {/* Create / edit role modal */}
      <Modal
        title={editingRole ? "Editar rol" : "Crear rol"}
        open={roleModalOpen}
        onCancel={() => setRoleModalOpen(false)}
        onOk={() => roleForm.submit()}
        confirmLoading={roleModalLoading}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={roleForm} layout="vertical" onFinish={handleSaveRole}>
          <Form.Item
            name="role_name"
            label="Nombre del rol"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Manage permissions modal */}
      <Modal
        title={permissionsRole ? `Permisos de ${permissionsRole.role_name}` : "Permisos"}
        open={!!permissionsRole}
        onCancel={() => setPermissionsRole(null)}
        footer={null}
      >
        <div className="flex flex-col gap-4 max-h-96 overflow-auto">
          {Array.from(permissionGroups.entries()).map(([objectName, objectPermissions]) => (
            <div key={objectName}>
              <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">
                {objectName}
              </div>
              <div className="flex flex-col gap-1">
                {objectPermissions.map((permission) => {
                  const checked = (permissionsRole?.permissions ?? []).some(
                    (p) => p.name === permission.name,
                  );
                  return (
                    <Checkbox
                      key={permission.id}
                      checked={checked}
                      disabled={
                        togglingPermissionId === permission.id ||
                        isSuperAdminRole(permissionsRole)
                      }
                      onChange={(e) =>
                        handleTogglePermission(permission, e.target.checked)
                      }
                    >
                      <Tag color={getActionColor(permission.display_action_name)}>
                        {permission.display_action_name}
                      </Tag>
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default RolesTab;
